import { z } from 'zod';

/**
 * Domain model for the daily routine. Deliberately small and platform-free;
 * persistence lives in OnEarly content types (see docs/decisions/0004-data.md).
 */
export const exerciseKindSchema = z.enum(['reading', 'writing', 'math']);
export type ExerciseKind = z.infer<typeof exerciseKindSchema>;

/** Fixed order of the daily routine — reading → math → writing. */
export const EXERCISE_ORDER: readonly ExerciseKind[] = ['reading', 'math', 'writing'];

export const DEFAULT_EXERCISE_MINUTES = 10;
/** Lengths Account › Exercise duration offers, in minutes. */
export const EXERCISE_MINUTE_OPTIONS: readonly number[] = [5, 10, 15, 20];

export const exerciseStatusSchema = z.enum(['todo', 'in_progress', 'done']);
export type ExerciseStatus = z.infer<typeof exerciseStatusSchema>;

export const dailyExerciseSchema = z.object({
  kind: exerciseKindSchema,
  title: z.string(),
  subtitle: z.string(),
  minutes: z.number().int().positive().default(DEFAULT_EXERCISE_MINUTES),
  status: exerciseStatusSchema.default('todo'),
});
export type DailyExercise = z.infer<typeof dailyExerciseSchema>;

export const dailyPlanSchema = z.object({
  /** ISO date (YYYY-MM-DD) in the user's local timezone. */
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  exercises: z.array(dailyExerciseSchema).length(3),
});
export type DailyPlan = z.infer<typeof dailyPlanSchema>;

export function completedCount(plan: DailyPlan): number {
  return plan.exercises.filter((exercise) => exercise.status === 'done').length;
}

export function isPlanComplete(plan: DailyPlan): boolean {
  return completedCount(plan) === plan.exercises.length;
}

/** The next exercise to do, in routine order; `undefined` when the day is done. */
export function nextExercise(plan: DailyPlan): DailyExercise | undefined {
  for (const kind of EXERCISE_ORDER) {
    const exercise = plan.exercises.find((candidate) => candidate.kind === kind);
    if (exercise && exercise.status !== 'done') return exercise;
  }
  return undefined;
}

/**
 * Home groups the routine into what to do now, what follows, and what is already done
 * (Figma Home: "Current" / "Up next" / "Completed"), each in routine order.
 */
export interface GroupedExercises {
  current: DailyExercise | undefined;
  upNext: DailyExercise[];
  completed: DailyExercise[];
}

export function groupExercises(plan: DailyPlan): GroupedExercises {
  const ordered = EXERCISE_ORDER.flatMap((kind) => {
    const exercise = plan.exercises.find((candidate) => candidate.kind === kind);
    return exercise ? [exercise] : [];
  });
  const completed = ordered.filter((exercise) => exercise.status === 'done');
  const [current, ...upNext] = ordered.filter((exercise) => exercise.status !== 'done');
  return { current, upNext, completed };
}

/** The exercise that follows `kind` in the routine, or `undefined` after math. */
export function followingExercise(kind: ExerciseKind): ExerciseKind | undefined {
  const index = EXERCISE_ORDER.indexOf(kind);
  return EXERCISE_ORDER[index + 1];
}

/**
 * Current streak from a list of ISO dates on which the routine was completed.
 * A streak counts back from `today`, or from yesterday if today isn't done yet
 * (the day isn't lost until it ends).
 */
export function currentStreak(completedDates: readonly string[], today: string): number {
  const done = new Set(completedDates);
  let cursor = done.has(today) ? today : previousDay(today);
  let streak = 0;
  while (done.has(cursor)) {
    streak += 1;
    cursor = previousDay(cursor);
  }
  return streak;
}

/** 0 = Monday … 6 = Sunday, for an ISO date (calendar day, timezone-free). */
export function weekdayIndex(isoDate: string): number {
  const [year, month, day] = isoDate.split('-').map(Number) as [number, number, number];
  return (new Date(Date.UTC(year, month - 1, day)).getUTCDay() + 6) % 7;
}

/**
 * Which days of `today`'s week (Monday → Sunday) the routine was completed on — the
 * streak card's filled circles. Only dates in the current week count.
 */
export function weekDoneIndexes(completedDates: readonly string[], today: string): number[] {
  const done = new Set(completedDates);
  const todayIndex = weekdayIndex(today);
  const week: string[] = [];
  let cursor = today;
  for (let index = todayIndex; index >= 0; index -= 1) {
    week[index] = cursor;
    cursor = previousDay(cursor);
  }
  return week.flatMap((date, index) => (done.has(date) ? [index] : []));
}

export function previousDay(isoDate: string): string {
  const [year, month, day] = isoDate.split('-').map(Number) as [number, number, number];
  const date = new Date(Date.UTC(year, month - 1, day));
  date.setUTCDate(date.getUTCDate() - 1);
  return date.toISOString().slice(0, 10);
}

/** Sums per math session (Figma shows one card and a "Next question" button). */
export const MATH_SESSION_LENGTH = 5;

export interface MathQuestion {
  a: number;
  b: number;
  op: '+' | '-';
  answer: number;
  /** Four choices, the answer among them, in a stable order. */
  options: number[];
}

/** Deterministic PRNG (mulberry32) so a session is stable for a given seed and testable. */
function mulberry32(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Level 1 sum ("Addition & subtraction", two digits): 24 + 37 = ? with four options.
 * Seed with e.g. the day number + question index so a session doesn't repeat itself.
 */
export function mathQuestion(seed: number): MathQuestion {
  const random = mulberry32(seed);
  const int = (min: number, max: number) => min + Math.floor(random() * (max - min + 1));
  const op: '+' | '-' = random() < 0.5 ? '+' : '-';
  let a = int(11, 49);
  let b = int(11, 49);
  if (op === '-' && b > a) [a, b] = [b, a];
  const answer = op === '+' ? a + b : a - b;
  const options = new Set([answer]);
  while (options.size < 4) {
    const offset = int(1, 9) * (random() < 0.5 ? -1 : 1);
    const candidate = answer + offset;
    if (candidate >= 0) options.add(candidate);
  }
  const shuffled = [...options];
  for (let i = shuffled.length - 1; i > 0; i -= 1) {
    const j = Math.floor(random() * (i + 1));
    const a2 = shuffled[i] as number;
    shuffled[i] = shuffled[j] as number;
    shuffled[j] = a2;
  }
  return { a, b, op, answer, options: shuffled };
}

export function formatClock(totalSeconds: number): string {
  const clamped = Math.max(0, Math.floor(totalSeconds));
  const minutes = Math.floor(clamped / 60);
  const seconds = clamped % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}
